import type { SummaryResponse, SummaryStatus, EncryptedPayload, PersonSummary, RiskAssessment, RedFlag, DataGap, ContextualFinding, RiskLevel } from "~/types/global";
import { SummaryModel } from "root/domain/entities/summary.model";
import { isPersonSummary, isNestedSummaryBySection } from "~/helpers/summary-type-guards.helper";

/**
 * DTO for handling Summary API responses
 * Validates and transforms the raw API response into a SummaryModel
 */
export class SummaryOutputDTO {
  private readonly summaryModel: SummaryModel;

  constructor(data: unknown) {
    const validated = this.validateAndTransform(data);
    this.summaryModel = new SummaryModel(validated);
  }

  private validateAndTransform(data: unknown): SummaryResponse {
    if (!data || typeof data !== "object") {
      console.error("Invalid data format for summary: expected an object", data);
      throw new Error("Erro ao buscar sumário executivo.");
    }

    const response = data as Record<string, any>;
    if (import.meta.env.DEV) {
      console.log("Raw summary response:", response);
    }

    // Quando a geração falha, o backend pode entregar os detalhes do erro dentro
    // de `data` ({ error_code, error_message }) em vez de uma estrutura de
    // tópicos. Eleva esses campos para o nível superior (apenas se ausentes) e
    // descarta o `data` não-tópico, para que a resposta de erro NÃO seja
    // rejeitada pela validação de estrutura abaixo (o que faria a UI exibir o
    // botão "Criar sumário" em vez de "Tentar novamente").
    this.extractErrorDataFallback(response);

    // Validate required fields
    if (!this.isValidSummaryResponse(response)) {
      console.error("Invalid summary response structure", response);
      throw new Error("Erro ao buscar sumário executivo.");
    }

    // Filtra tópicos inválidos ao invés de rejeitar toda a resposta
    if (response.data && typeof response.data === "object" && !this.isValidEncryptedPayload(response.data)) {
      response.data = this.sanitizeSummaryData(response.data);
    }
    // Normaliza os campos de erro APÓS a validação dos campos obrigatórios.
    response.error_code = this.normalizeErrorField(response.error_code, 256);
    response.error_message = this.normalizeErrorField(response.error_message, 5000);

    return response as SummaryResponse;
  }

  /**
   * Normaliza a resposta de erro do sumário.
   *
   * Quando `summary_status === "error"` (ou quando `data` carrega campos de erro
   * explícitos), o backend pode entregar `data` como `{ error_code,
   * error_message }` em vez de uma estrutura de tópicos. Este método:
   *
   * 1. Detecta `data` que NÃO é uma estrutura de tópicos válida nem um payload
   *    criptografado.
   * 2. Eleva `error_message` / `error_code` de `data` para o nível superior,
   *    apenas quando o campo de nível superior estiver ausente (a mensagem de
   *    nível superior, vinda do banco, tem prioridade).
   * 3. Zera `data` (uma resposta de erro não possui tópicos exibíveis).
   *
   * Mantém o comportamento de rejeição apenas para respostas NÃO-erro com `data`
   * genuinamente corrompido. Total: nunca lança.
   */
  private extractErrorDataFallback(response: Record<string, any>): void {
    const data = response.data;

    if (data == null) {
      return;
    }

    // Estrutura de tópicos válida ou payload criptografado → fluxo normal.
    if (this.hasValidStructure(data) || this.isValidEncryptedPayload(data)) {
      return;
    }

    const isErrorStatus = response.summary_status === "error";
    const hasErrorFields =
      typeof data === "object" &&
      (typeof data.error_message === "string" || typeof data.error_code === "string");

    // Só normaliza quando for uma resposta de erro ou quando `data` carrega
    // campos de erro explícitos. Caso contrário, deixa a validação seguir
    // (e potencialmente rejeitar) para não mascarar payloads corrompidos.
    if (!isErrorStatus && !hasErrorFields) {
      return;
    }

    if (typeof data === "object") {
      if (response.error_message == null && typeof data.error_message === "string") {
        response.error_message = data.error_message;
      }
      if (response.error_code == null && typeof data.error_code === "string") {
        response.error_code = data.error_code;
      }
    }

    // Resposta de erro não tem tópicos exibíveis.
    response.data = null;
  }

  /**
   * Normaliza campos de erro garantindo que sejam strings não vazias
   * com comprimento máximo definido.
   * @param value Valor a ser normalizado
   * @param maxLength Comprimento máximo permitido
   * @returns String normalizada ou undefined se inválida
   */
  private normalizeErrorField(value: unknown, maxLength: number): string | undefined {
    if (typeof value !== "string") {
      return undefined;
    }

    const trimmed = value.trim();

    if (trimmed.length < 1 || trimmed.length > maxLength) {
      return undefined;
    }

    return trimmed;
  }

  private isValidSummaryResponse(data: any): data is SummaryResponse {
    const requiredFields = [
      'summary_id',
      'report_id',
      'summary_status'
    ] as const;

    // Check if all required fields exist
    if (!requiredFields.every(field => field in data)) {
      console.error("Missing required fields in summary response");
      return false;
    }

    // Validate field types - summary_id can be string or null
    if (data.summary_id !== null && typeof data.summary_id !== "string") {
      console.error("Invalid summary_id type");
      return false;
    }

    if (typeof data.report_id !== "string") {
      console.error("Invalid report_id type");
      return false;
    }

    // Validate status - can be null or one of the valid statuses
    const validStatuses: SummaryStatus[] = ["pending", "success", "error", null];
    if (!validStatuses.includes(data.summary_status)) {
      console.error("Invalid summary_status value");
      return false;
    }

    // Validate report_name (can be string or encrypted)
    if (data.report_name !== undefined && data.report_name !== null) {
      const isValidName = typeof data.report_name === "string" ||
        this.isValidEncryptedPayload(data.report_name);
      if (!isValidName) {
        console.error("Invalid report_name type");
        return false;
      }
    }

    // Validate data field if present
    if (data.data !== null && data.data !== undefined) {
      const isValidData = this.hasValidStructure(data.data) ||
        this.isValidEncryptedPayload(data.data);
      if (!isValidData) {
        console.error("Invalid data structure");
        return false;
      }
    }

    return true;
  }

  /**
   * Verifica se os dados têm a estrutura básica correta (sem validar cada tópico)
   */
  private hasValidStructure(data: any): boolean {
    if (!data || typeof data !== "object") return false;
    if (!("topics" in data) || !Array.isArray(data.topics)) return false;
    return true;
  }

  /**
   * Sanitiza os dados removendo tópicos inválidos e expandindo tópicos
   * com summary_by_section aninhado (Múltiplos Registros) em N+1 tópicos planos.
   */
  private sanitizeSummaryData(data: any): any {
    if (!data || typeof data !== "object") return data;
    if (!Array.isArray(data.topics)) return data;

    const expandedTopics: any[] = [];

    for (const topic of data.topics) {
      const isValid = this.isValidTopic(topic);
      if (!isValid) {
        if (topic?.title) {
          console.warn(`Topic "${topic.title}" has invalid format and will be filtered out`);
        }
        continue;
      }

      // Normalize optional fields after validation passes
      this.normalizeTopic(topic);

      // Check if this topic has nested summary_by_section (Múltiplos Registros)
      if (
        topic.summary_by_section &&
        typeof topic.summary_by_section === "object" &&
        isNestedSummaryBySection(topic.summary_by_section)
      ) {
        const nestedTopics = this.expandNestedTopic(topic);
        expandedTopics.push(...nestedTopics);
      } else {
        expandedTopics.push(topic);
      }
    }

    return {
      ...data,
      topics: expandedTopics,
      risk_assessment: this.validateRiskAssessment(data.risk_assessment),
      red_flags: this.validateRedFlags(data.red_flags),
      compliance_observations: this.validateStringArray(data.compliance_observations),
      recommended_next_steps: this.validateStringArray(data.recommended_next_steps),
      data_gaps: this.validateDataGaps(data.data_gaps),
    };
  }

  /**
   * Expande um tópico com summary_by_section aninhado (Record<string, PersonSummary>)
   * em N+1 tópicos planos:
   * - 1 tópico pai: person_name → executive_summary_text
   * - N tópicos filhos: um por pessoa, com summary_by_section mesclado de todos os inner topics
   */
  private expandNestedTopic(topic: any): any[] {
    const nestedSummary = topic.summary_by_section as Record<string, PersonSummary>;
    const parentTopicId = topic.id || "t-Múltiplos Registros Encontrados";
    const results: any[] = [];

    // Build parent topic: person_name → executive_summary_text (flat Record<string, string>)
    const parentSummaryBySection: Record<string, string> = {};
    for (const [personName, personSummary] of Object.entries(nestedSummary)) {
      parentSummaryBySection[personName] = personSummary.executive_summary || "";
    }

    const parentTopic = {
      ...topic,
      id: "t-Múltiplos Registros Encontrados",
      title: topic.title || "Múltiplos Registros Encontrados",
      related_sections: topic.related_sections || ["Múltiplos Registros Encontrados"],
      summary_by_section: parentSummaryBySection,
      _nested_summary_by_section: nestedSummary,
    };
    results.push(parentTopic);

    // Build N child topics: one per person
    const personEntries = Object.entries(nestedSummary);
    personEntries.forEach(([personName, personSummary], index) => {
      // Merge all inner topics' summary_by_section into a single flat Record<string, string>
      const mergedSummaryBySection: Record<string, string> = {};
      if (Array.isArray(personSummary.topics)) {
        for (const innerTopic of personSummary.topics) {
          if (innerTopic.summary_by_section && typeof innerTopic.summary_by_section === "object") {
            for (const [sectionName, sectionSummary] of Object.entries(innerTopic.summary_by_section)) {
              if (typeof sectionSummary === "string") {
                mergedSummaryBySection[sectionName] = sectionSummary;
              }
            }
          }
        }
      }

      const childTopic = {
        id: `t-multiplos-person-${index}`,
        title: personName,
        related_sections: topic.related_sections || ["Múltiplos Registros Encontrados"],
        summary_by_section: mergedSummaryBySection,
        tags: topic.tags || [],
        entities: topic.entities || [],
        _parentTopicId: parentTopicId,
      };
      results.push(childTopic);
    });

    return results;
  }

  private isValidTopic(topic: any): boolean {
    if (!topic || typeof topic !== "object") return false;

    if (typeof topic.id !== "string") return false;
    if (typeof topic.title !== "string") return false;
    if (!Array.isArray(topic.related_sections)) return false;

    // Valida summary_by_section se presente
    if (topic.summary_by_section !== undefined && topic.summary_by_section !== null) {
      if (!this.isValidSummaryBySection(topic.summary_by_section)) {
        console.warn(`Topic "${topic.title}" has invalid summary_by_section format, will be filtered out`);
        return false;
      }
    }

    return true;
  }

  /**
   * Normalizes a validated topic's optional fields (tags, entities, contextual_findings).
   * Called AFTER isValidTopic returns true — keeps validation pure (no mutations).
   */
  private normalizeTopic(topic: any): void {
    // Normaliza tags: filtra itens que não são strings não-vazias
    if (Array.isArray(topic.tags)) {
      topic.tags = topic.tags.filter(
        (tag: unknown): tag is string => typeof tag === "string" && tag.trim().length > 0
      );
    } else {
      topic.tags = [];
    }

    // Normaliza entities: filtra itens sem type/name string
    if (Array.isArray(topic.entities)) {
      topic.entities = topic.entities.filter(
        (e: unknown): boolean =>
          e != null &&
          typeof e === "object" &&
          typeof (e as any).type === "string" &&
          typeof (e as any).name === "string"
      );
    } else {
      topic.entities = [];
    }

    // Normaliza contextual_findings
    if (topic.contextual_findings !== undefined && topic.contextual_findings !== null) {
      topic.contextual_findings = this.validateContextualFindings(topic.contextual_findings);
    }
  }

  /**
   * Valida se summary_by_section está no formato correto:
   * - Flat: Record<string, string> (todos os valores são strings não vazias)
   * - Nested: Record<string, PersonSummary> (todos os valores são objetos com executive_summary e topics)
   * Formatos mistos (alguns string, alguns objeto) são rejeitados.
   */
  private isValidSummaryBySection(value: unknown): value is Record<string, string> | Record<string, PersonSummary> {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const entries = Object.entries(value);

    // Deve ter pelo menos uma entrada
    if (entries.length === 0) {
      return false;
    }

    // Verifica se todos os valores são strings (flat format)
    const allStrings = entries.every(([key, val]) =>
      typeof key === 'string' &&
      typeof val === 'string' &&
      val.trim().length > 0
    );

    if (allStrings) {
      return true;
    }

    // Verifica se todos os valores são PersonSummary (nested format)
    const allNested = entries.every(([key, val]) =>
      typeof key === 'string' &&
      isPersonSummary(val)
    );

    if (allNested) {
      return true;
    }

    // Formato misto — rejeitado
    return false;
  }

  // ─── Metadata field validators ────────────────────────────────────────────

  private static readonly VALID_RISK_LEVELS: RiskLevel[] = ["critical", "high", "medium", "low"];

  private validateRiskAssessment(value: unknown): RiskAssessment | undefined {
    if (!value || typeof value !== "object") return undefined;
    const obj = value as Record<string, unknown>;
    if (
      typeof obj.overall_risk_level === "string" &&
      SummaryOutputDTO.VALID_RISK_LEVELS.includes(obj.overall_risk_level as RiskLevel) &&
      typeof obj.justification === "string"
    ) {
      return {
        overall_risk_level: obj.overall_risk_level as RiskLevel,
        justification: obj.justification,
      };
    }
    return undefined;
  }

  private validateRedFlags(value: unknown): RedFlag[] | undefined {
    if (!Array.isArray(value)) return undefined;
    return value.filter(
      (item): item is RedFlag =>
        item != null &&
        typeof item === "object" &&
        typeof item.flag === "string" &&
        item.evidence != null &&
        typeof item.evidence === "object" &&
        typeof item.evidence.section === "string" &&
        typeof item.evidence.field === "string"
    );
  }

  private validateStringArray(value: unknown): string[] | undefined {
    if (!Array.isArray(value)) return undefined;
    const filtered = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return filtered.length > 0 ? filtered : undefined;
  }

  private validateDataGaps(value: unknown): DataGap[] | undefined {
    if (!Array.isArray(value)) return undefined;
    return value.filter(
      (item): item is DataGap =>
        item != null &&
        typeof item === "object" &&
        typeof item.gap === "string" &&
        typeof item.why_it_matters === "string"
    );
  }

  private validateContextualFindings(value: unknown): ContextualFinding[] {
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is ContextualFinding =>
        item != null &&
        typeof item === "object" &&
        typeof item.finding === "string" &&
        typeof item.risk_level === "string" &&
        SummaryOutputDTO.VALID_RISK_LEVELS.includes(item.risk_level as RiskLevel) &&
        item.evidence != null &&
        typeof item.evidence === "object" &&
        typeof item.evidence.section === "string" &&
        typeof item.evidence.field === "string" &&
        typeof item.why_it_matters_in_context === "string"
    );
  }

  // ─── End metadata validators ────────────────────────────────────────────

  private isValidEncryptedPayload(data: any): data is EncryptedPayload {
    return (
      data != null &&
      typeof data === "object" &&
      "encrypted" in data &&
      "iv" in data &&
      typeof data.encrypted === "string" &&
      typeof data.iv === "string"
    );
  }

  /**
   * Get the summary model
   */
  public getSummaryModel(): SummaryModel {
    return this.summaryModel;
  }

  /**
   * Get the raw summary response
   */
  public getRaw(): SummaryResponse {
    return this.summaryModel.raw;
  }

  /**
   * Get the summary ID
   */
  public getSummaryId(): string {
    return this.summaryModel.summaryId;
  }

  /**
   * Get the report ID
   */
  public getReportId(): string {
    return this.summaryModel.reportId;
  }

  /**
   * Get the summary status
   */
  public getStatus(): SummaryStatus {
    return this.summaryModel.summaryStatus;
  }

  /**
   * Check if the summary is successfully generated
   */
  public isSuccess(): boolean {
    return this.summaryModel.isSuccess;
  }

  /**
   * Check if the summary is pending
   */
  public isPending(): boolean {
    return this.summaryModel.isPending;
  }

  /**
   * Check if the summary has an error
   */
  public isError(): boolean {
    return this.summaryModel.isError;
  }

  /**
   * Check if the summary has data
   */
  public hasData(): boolean {
    return this.summaryModel.hasData;
  }

  /**
   * Get the number of topics
   */
  public getTopicsCount(): number {
    return this.summaryModel.topicsCount;
  }

  /**
   * Get normalized summary data
   */
  public toJSON() {
    return this.summaryModel.toJSON();
  }
}
